/**
 * 파일명 : evl0109m.tsx
 * 설명 : 인사평가 매핑관리
 * 수정내역
 * **********************************************
 * Version    수정일자      수정자    수정내용
 * **********************************************
 * 1.0       2025-12-18   김한철    최초작성
 *
 * (수정) 2026-03-05 ChatGPT
 * - 공통 사이클/잠금 상태를 부모(Evl0109m)로 끌어올려 HR/프로젝트 탭에 props로 전달
 * - ProjectEvaluationTableContainer에서 evlCycleExists 등 참조 오류(스코프 에러) 해결
 */

/************************************************
 * Import 영역
 ************************************************/
/* React */
import React, { useEffect, useMemo, useCallback, useState } from "react";

/* AntD */
import {
  Tabs,
  Table,
  Input,
  Select,
  Button,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  message,
  Upload,
  Popconfirm,
  Modal,
  Switch,
} from "antd";
import {
  FileTextOutlined,
  ProjectOutlined,
  SearchOutlined,
  UploadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

/* 업무 */
import * as commonProxy from "libs/CommonProxy";
import * as common from "libs/common";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/* 프로젝트 평가 탭 컴포넌트 */
import { ProjectEvaluationTable } from "./evl0111m";

/* Redux */
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { Evl0109mAction } from "../../../redux/slice/evl/evl0109m_slice";
import type {
  HrMapRow,
  EmployeeSearchKey,
} from "../../../redux/models/evl/evl0109m_model";

/************************************************
 * 유틸
 ************************************************/
function buildCodeMap(datas: any[]): Record<string, string> {
  const map: Record<string, string> = {};
  (datas ?? []).forEach((d: any) => {
    const code = d?.cd ?? d?.CD ?? d?.code ?? d?.value;
    const name = d?.cdNm ?? d?.CD_NM ?? d?.name ?? d?.label;
    if (code != null) map[String(code)] = name ?? String(code);
  });
  return map;
}

/************************************************
 * 공통(사이클/잠금) Props 타입
 ************************************************/
type CycleProps = {
  evlCycleExists: boolean;
  hrEvlStDt: string;
  bizPlgStDt: string;
  eval2EdDt: string;
  isMappingModifyBlocked: boolean;
  isHrMappingLocked: boolean;
  lockMsg: string;
};

/************************************************
 * 인사평가 테이블 컴포넌트
 * ✅ 조회 방식 “원본 그대로” 복구 (절대 변경 금지)
 *
 * ✅ 변경사항(요청 반영):
 * - 템플릿 업로드 시 즉시 서버 업로드 ❌
 * - 엑셀 파싱 → 미리보기(모달 + 테이블) → 사용자가 “업로드” 눌렀을 때만 서버 업로드 ✅
 * - 서버 검증은 제외(프론트 공백 검증만)
 ************************************************/
function HrEvaluationTable(props: CycleProps) {
  const {
    evlCycleExists,
    hrEvlStDt,
    eval2EdDt,
    isMappingModifyBlocked,
    isHrMappingLocked,
    lockMsg,
  } = props;

  /************************************************
   * Redux / State
   ************************************************/
  const dispatch = useAppDispatch();

  const evlYear = useAppSelector((state) => state.Evl0109m.evlYear);
  const evlYearList = useAppSelector((state) => state.Evl0109m.evlYearList);

  const rows = useAppSelector((state) => state.Evl0109m.hrRows);
  const loading = useAppSelector((state) => state.Evl0109m.hrLoading);

  const empSearchKey = useAppSelector((state) => state.Evl0109m.empSearchKey);
  const searchText = useAppSelector((state) => state.Evl0109m.searchText);

  const posClsMap = useAppSelector((state) => state.Evl0109m.posClsMap);

  const [pageNo, setPageNo] = useState(1);

  // ✅ 업로드 미리보기 상태
  const [uploadPreviewOpen, setUploadPreviewOpen] = useState(false);
  const [uploadPreviewRows, setUploadPreviewRows] = useState<any[]>([]);
  const [uploadPreviewFileName, setUploadPreviewFileName] = useState("");
  const [uploading, setUploading] = useState(false);

  const [onlyExcluded, setOnlyExcluded] = useState(false);

  /************************************************
   * 유틸 / 판별 함수
   ************************************************/
  const isEvalStarted = (row: any) => {
    const v = (x: any) => String(x ?? "").toUpperCase();
    return (
      v(row.slfSubYn) === "Y" ||
      v(row.eval1SubYn) === "Y" ||
      v(row.eval2SubYn) === "Y"
    );
  };

  /************************************************
   * 공통코드 / 이름 변환
   ************************************************/
  const setPosClsList = useCallback(() => {
    const option = {
      useYn: "Y",
      callback: (datas: any[]) =>
        dispatch(Evl0109mAction.setPosClsMap(buildCodeMap(datas))),
    };
    common.getComCodeList("011", option);
  }, [dispatch]);

  const posName = useCallback(
    (cd?: string) => (cd ? posClsMap[cd] ?? cd : ""),
    [posClsMap]
  );

  /************************************************
   * 현재 목록 기준 "평가 진행중" 피평가자 Set
   ************************************************/
  const startedEmpNoSet = useMemo(() => {
    const set = new Set<string>();

    (rows ?? []).forEach((r) => {
      if (isEvalStarted(r)) {
        const empNo = String(r.empNo ?? "").trim();
        if (empNo) set.add(empNo);
      }
    });

    return set;
  }, [rows]);

  /************************************************
   * 서버 조회 (원본 그대로)
   ************************************************/
  const loadHrMapping = useCallback(
    async (opt?: {
      year?: string;
      searchKey?: EmployeeSearchKey;
      text?: string;
      showMsg?: boolean;
    }) => {
      const year = opt?.year ?? evlYear;
      const searchKey = opt?.searchKey ?? empSearchKey;
      const text = (opt?.text ?? searchText ?? "").trim();

      dispatch(Evl0109mAction.setHrLoading(true));

      try {
        const requestData: any = {
          svcId: "TREVLR0007",
          evlYear: year,
          searchKey,
          searchText: text,
        };

        await commonProxy.callAsyncSvc(requestData, {
          txCd: "TREVLR0007",
          success: (responseData: any) => {
            const rawList =
              responseData?.list ??
              responseData?.data?.list ??
              responseData?.hrMappingList ??
              responseData?.data?.hrMappingList ??
              [];

            const mapped: HrMapRow[] = Array.isArray(rawList) ? rawList : [];
            dispatch(Evl0109mAction.setHrRows(mapped));

            // ✅ 서버에서 내려준 평가년도 목록(evlList)
            const rawEvlList =
              responseData?.evlList ?? responseData?.data?.evlList ?? [];
            const years = (Array.isArray(rawEvlList) ? rawEvlList : [])
              .map((yy) => String(yy))
              .filter((yy) => yy.trim() !== "")
              .sort((a, b) => Number(b) - Number(a));

            dispatch(Evl0109mAction.setEvlYearList(years));

            // ✅ 현재 year가 비어있거나 목록에 없으면 최신년도(0번)로 보정
            if (years.length > 0 && (!year || !years.includes(year))) {
              dispatch(Evl0109mAction.setEvlYear(years[0]));
            }

            if (opt?.showMsg !== false) {
              if (mapped.length === 0) message.info("조회된 데이터가 없습니다.");
              else message.success(`조회 완료 (${mapped.length}건)`);
            }
          },
        });
      } catch (e) {
          
        message.error("조회 중 오류가 발생했습니다.");
      } finally {
        dispatch(Evl0109mAction.setHrLoading(false));
      }
    },
    [dispatch, evlYear, empSearchKey, searchText]
  );

  /************************************************
   * 매핑 자동생성
   ************************************************/
  const onCreateHrMappingQuery = useCallback(async () => {
    try {
      common.showLoading(true);

      const requestData: any = {
        svcId: "TREVLS0008",
        evlYear,
      };

      commonProxy.callAsyncSvc(requestData, {
        txCd: "TREVLS0008",
        success: async (response: any) => {
          const cnt = response?.count ?? 0;
          const created = response?.created ?? false;

          if (created && cnt > 0) {
            message.success(`인사평가 매핑이 ${cnt}건 생성되었습니다.`);
            await loadHrMapping({ showMsg: false });
          } else {
            message.info("추가로 생성할 인사평가 매핑이 없습니다.");
          }
        },
        error: (errorMsg: string) => {
          message.error(errorMsg || "서버 통신 중 오류가 발생했습니다.");
        },
      });
    } catch (e) {
        
      message.error("쿼리 생성 요청 중 오류가 발생했습니다.");
    } finally {
      common.showLoading(false);
    }
  }, [evlYear, loadHrMapping]);

  /************************************************
   * 미리보기 표시 Rows
   ************************************************/
  const previewTableRows = useMemo(() => {
    return onlyExcluded
      ? uploadPreviewRows.filter((r) => !r._valid)
      : uploadPreviewRows;
  }, [onlyExcluded, uploadPreviewRows]);

  /************************************************
   * 엑셀 업로드(미리보기 → 확정 업로드)
   ************************************************/
  const parseHrExcelForPreview = useCallback(
    async (file: File) => {
      const ab = await file.arrayBuffer();
      const wb = XLSX.read(ab, { type: "array" });

      const wsTpl = wb.Sheets["HR템플릿"];
      if (!wsTpl) throw new Error("시트 'HR템플릿' 을 찾을 수 없습니다.");

      // ✅ HR템플릿 A2부터
      const tplRows = XLSX.utils.sheet_to_json<any>(wsTpl, {
        range: 1,
        header: [
          "성명",
          "사업부",
          "팀",
          "직급",
          "1차평가자명",
          "2차평가자명",
          "피평가자사번",
          "1차평가자사번",
          "2차평가자사번",
        ],
        defval: "",
        raw: false,
      });

      // ✅ 빈행 제거(사번/성명 둘 다 없으면 스킵)
      const filtered = (tplRows ?? []).filter((r: any) => {
        const empNo = String(r["피평가자사번"] ?? "").trim();
        const empNm = String(r["성명"] ?? "").trim();
        const eval1EmpNo = String(r["1차평가자사번"] ?? "").trim();
        const eval2EmpNo = String(r["2차평가자사번"] ?? "").trim();
        return empNo || empNm || eval1EmpNo || eval2EmpNo;
      });

      const previewRows = filtered.map((r: any, idx: number) => {
        const rowNo = idx + 2;

        const empNo = String(r["피평가자사번"] ?? "").trim();
        const empNm = String(r["성명"] ?? "").trim();
        const eval1EmpNo = String(r["1차평가자사번"] ?? "").trim();
        const eval2EmpNo = String(r["2차평가자사번"] ?? "").trim();

        const empDeptNm = String(r["팀"] ?? "").trim();
        const empPosNm = String(r["직급"] ?? "").trim();

        const eval1Nm = String(r["1차평가자명"] ?? "").trim();
        const eval2Nm = String(r["2차평가자명"] ?? "").trim();

        const errors: string[] = [];
        const empNoPattern = /^\d{3}M\d{4}\d{3}$/;

        if (!empNo) errors.push("피평가자사번 누락");
        else if (!empNoPattern.test(empNo)) errors.push("피평가자사번 형식오류");

        if (!eval1EmpNo) errors.push("1차평가자사번 누락");
        else if (!empNoPattern.test(eval1EmpNo)) errors.push("1차평가자사번 형식오류");

        if (!eval2EmpNo) errors.push("2차평가자사번 누락");
        else if (!empNoPattern.test(eval2EmpNo)) errors.push("2차평가자사번 형식오류");

        if (startedEmpNoSet.has(empNo)) {
          errors.push("평가 진행중");
        }

        return {
          key: `${file.name}-${rowNo}`,
          _rowNo: rowNo,

          evlYear,
          empNo,
          eval1EmpNo,
          eval2EmpNo,

          // ✅ 미리보기 표시값
          empNm,
          empDeptNm,
          empPosNm,
          eval1Nm,
          eval2Nm,

          _errors: errors,
          _valid: errors.length === 0,
        };
      });

      return previewRows;
    },
    [evlYear, startedEmpNoSet]
  );

  const commitHrExcelUpload = useCallback(async () => {
    if (!uploadPreviewRows.length) {
      message.warning("업로드할 데이터가 없습니다.");
      return;
    }

    const uploadTargets = uploadPreviewRows.filter((r) => r._valid);
    if (uploadTargets.length === 0) {
      message.warning("업로드 가능한 데이터가 없습니다. (모든 행에 오류가 있습니다.)");
      return;
    }

    try {
      setUploading(true);
      common.showLoading(true);

      const requestData: any = {
        svcId: "TREVLS0011",
        list: uploadTargets.map((r) => ({
          evlYear: r.evlYear,
          empNo: r.empNo,
          eeDeptCd: "",
          eePosClsCd: "",
          eval1EmpNo: r.eval1EmpNo,
          eval2EmpNo: r.eval2EmpNo,
        })),
      };

      await commonProxy.callAsyncSvc(requestData, {
        txCd: "TREVLS0011",
        success: async () => {
          message.success(`엑셀 업로드 완료 (${uploadTargets.length}건)`);
          setUploadPreviewOpen(false);
          setUploadPreviewRows([]);
          setOnlyExcluded(false);
          await loadHrMapping({ showMsg: false });
        },
      });
    } catch (e) {
        
      message.error("업로드 중 오류가 발생했습니다.");
    } finally {
      setUploading(false);
      common.showLoading(false);
    }
  }, [uploadPreviewRows, loadHrMapping]);

  /************************************************
   * 엑셀 다운로드(복구 버전)
   ************************************************/
  const onDownloadHrExcel = useCallback(async () => {
    try {
      common.showLoading(true);

      if (!rows || rows.length === 0) {
        message.warning("다운로드할 데이터가 없습니다.");
        return;
      }

      const excelRows = rows.map((r: HrMapRow) => {
        const eePosNm = r.eePosClsCd ? posName(r.eePosClsCd) || r.eePosClsCd : "";
        return {
          성명: r.userNm ?? "",
          사업부: r.eeUpperDeptNm ?? "",
          팀: r.deptNm ?? "",
          직급: eePosNm,
          평가자1차: r.eval1EmpNm ?? "",
          평가자2차: r.eval2EmpNm ?? "",
        };
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelRows);

      ws["!cols"] = [
        { wch: 12 },
        { wch: 18 },
        { wch: 20 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
      ];

      XLSX.utils.book_append_sheet(wb, ws, "HR_MAPPING");

      const fileName = `인사평가매핑_${evlYear || new Date().getFullYear()}.xlsx`;
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], { type: "application/octet-stream" });
      saveAs(blob, fileName);

      message.success(`엑셀 다운로드 완료 (${excelRows.length}건)`);
    } catch (e) {
        
      message.error("엑셀 다운로드 중 오류가 발생했습니다.");
    } finally {
      common.showLoading(false);
    }
  }, [rows, evlYear, posName]);

  /************************************************
   * 템플릿 다운로드(개선 버전)
   ************************************************/
  const onDownloadTemplate = useCallback(async () => {
    try {
      common.showLoading(true);

      const requestData: any = { svcId: "TREVLR0020" };

      await commonProxy.callAsyncSvc(requestData, {
        txCd: "TREVLR0020",
        success: (res: any) => {
          const data = res?.data ?? res ?? {};
          const userList = data?.userList ?? [];

          const templateHeaderKor = [
            "성명",
            "사업부",
            "팀",
            "직급",
            "1차평가자명",
            "2차평가자명",
            "피평가자사번",
            "1차평가자사번",
            "2차평가자사번",
          ];

          const hrTemplateAoaRows = (rows ?? []).map((r: HrMapRow) => {
            const eePosNm = r.eePosClsCd ? posName(r.eePosClsCd) || r.eePosClsCd : "";
            const eval1Nm = r.eval1EmpNm ?? r.eval1EmpNo ?? "";
            const eval2Nm = r.eval2EmpNm ?? r.eval2EmpNo ?? "";

            return [
              r.userNm ?? "",
              r.eeUpperDeptNm ?? "",
              r.deptNm ?? "",
              eePosNm,
              eval1Nm,
              eval2Nm,
              r.empNo ?? "",
              r.eval1EmpNo ?? "",
              r.eval2EmpNo ?? "",
            ];
          });

          const users = (userList ?? []).map((u: any) => ({
            USER_NM: u.userNm ?? u.USER_NM ?? "",
            EMP_NO: u.empNo ?? u.EMP_NO ?? "",
            DEPT_NM: u.deptNm ?? u.DEPT_NM ?? "",
            POST_NM: u.postNm ?? u.POST_NM ?? "",
          }));

          const wb = XLSX.utils.book_new();

          // 1) HR템플릿
          const ws1 = XLSX.utils.aoa_to_sheet([templateHeaderKor]);
          if (hrTemplateAoaRows.length > 0) {
            XLSX.utils.sheet_add_aoa(ws1, hrTemplateAoaRows, { origin: "A2" });
          }

          ws1["!cols"] = [
            { wch: 14 },
            { wch: 18 },
            { wch: 18 },
            { wch: 12 },
            { wch: 16 },
            { wch: 16 },
            { wch: 16 },
            { wch: 16 },
            { wch: 16 },
          ];
          XLSX.utils.book_append_sheet(wb, ws1, "HR템플릿");

          // 2) 사원목록
          const ws2 = XLSX.utils.json_to_sheet(users, {
            header: ["USER_NM", "DEPT_NM", "POST_NM", "EMP_NO"],
          });
          XLSX.utils.sheet_add_aoa(ws2, [["사원명", "팀", "직급명", "사원번호"]], {
            origin: "A1",
          });
          XLSX.utils.book_append_sheet(wb, ws2, "사원목록");

          const now = new Date();
          const y = now.getFullYear();
          const m = String(now.getMonth() + 1).padStart(2, "0");
          const d = String(now.getDate()).padStart(2, "0");
          const fileName = `인사평가_매핑템플릿_${y}${m}${d}.xlsx`;

          const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
          const blob = new Blob([wbout], { type: "application/octet-stream" });
          saveAs(blob, fileName);

          message.success(`HR템플릿 다운로드 완료 (TEMPLATE ${hrTemplateAoaRows.length}건)`);
        },
      });
    } catch (e) {
        
      message.error("템플릿 다운로드 중 오류가 발생했습니다.");
    } finally {
      common.showLoading(false);
    }
  }, [rows, posName]);

  /************************************************
   * 초기 실행
   ************************************************/
  useEffect(() => {
    setPosClsList();
  }, [setPosClsList]);

  useEffect(() => {
    loadHrMapping({ showMsg: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /************************************************
   * 검색/필터
   ************************************************/
  const yearOptions = useMemo(() => {
    if (!evlYearList || evlYearList.length === 0) return [];
    return evlYearList.map((y) => ({ label: y, value: y }));
  }, [evlYearList]);

  const openEvalPopup = useCallback(
    (record: HrMapRow, which: "eval1" | "eval2") => {
      const isDone =
        which === "eval1" ? record.eval1SubYn === "Y" : record.eval2SubYn === "Y";
      const doneLabel = which === "eval1" ? "1차" : "2차";

      if (isHrMappingLocked) {
        message.warning(lockMsg);
        return;
      }
      if (isEvalStarted(record)) {
        message.warning("이미 평가가 진행된 데이터는 변경할 수 없습니다. (제출 취소 후 가능)");
        return;
      }

      const accept = async (targets: any[]) => {
        const picked = targets?.[0];
        if (!picked?.apprEmpNo) {
          message.warning("선택된 사용자가 없습니다.");
          return;
        }

        const doSave = async () => {
          const requestData: any = {
            svcId: "TREVLS0013",
            hrMapId: record.hrMapId,
            which,
            newEvalEmpNo: picked.apprEmpNo,
          };

          try {
            common.showLoading(true);
            await commonProxy.callAsyncSvc(requestData, {
              txCd: "TREVLS0013",
              success: async () => {
                message.success(`${doneLabel} 평가자 저장 완료`);
                await loadHrMapping({ showMsg: false });
              },
            });
          } catch (e) {
              
            message.error("평가자 저장 중 오류가 발생했습니다.");
          } finally {
            common.showLoading(false);
          }
        };

        if (isDone) {
          Modal.confirm({
            title: `${doneLabel} 평가가 이미 완료된 건입니다.`,
            content: `평가자를 변경하면 기존 평가 결과가 영향을 받을 수 있습니다. 계속 진행할까요?)`,
            okText: "변경",
            cancelText: "취소",
            centered: true,
            onOk: doSave,
          });
          return;
        }

        await doSave();
      };

      common.openPopup({
        title: which === "eval1" ? "1차 평가자 선택" : "2차 평가자 선택",
        popupId: "evl0109p",
        accept,
        cancel: () => {},
        width: 1350,
        props: {
          data: {
            mode: "EVL_EVALUATOR_PICK",
            which,
            currentEmpNo: which === "eval1" ? record.eval1EmpNo : record.eval2EmpNo,
            eeEmpNo: record.empNo,
            eeUserNm: record.userNm,
          },
        },
      } as any);
    },
    [isHrMappingLocked, lockMsg, loadHrMapping]
  );

  const onSearch = useCallback(() => {
    setPageNo(1);
    loadHrMapping({ showMsg: true });
  }, [loadHrMapping]);

  const onResetSearch = useCallback(() => {
    setPageNo(1);
    dispatch(Evl0109mAction.resetSearch());
    loadHrMapping({
      year: "",
      searchKey: "all",
      text: "",
      showMsg: true,
    });
  }, [dispatch, loadHrMapping]);

  const onChangeYear = useCallback(
    (y: string) => dispatch(Evl0109mAction.setEvlYear(y)),
    [dispatch]
  );
  const onChangeSearchKey = useCallback(
    (k: EmployeeSearchKey) => dispatch(Evl0109mAction.setEmpSearchKey(k)),
    [dispatch]
  );
  const onChangeSearchText = useCallback(
    (v: string) => dispatch(Evl0109mAction.setSearchText(v)),
    [dispatch]
  );

  /************************************************
   * 단건 삭제
   ************************************************/
  const deleteMappingOne = useCallback(
    async (row: any) => {
      if (isHrMappingLocked) {
        message.warning(lockMsg);
        return;
      }
      if (!row) return;

      if (isEvalStarted(row)) {
        message.warning("이미 평가가 진행된 데이터는 삭제할 수 없습니다.");
        return;
      }

      try {
        common.showLoading(true);

        const requestData: any = {
          svcId: "TREVLS0024",
          evlYear: row.evlYear,
          empNo: row.empNo,
          hrMapId: row.hrMapId,
        };

        await commonProxy.callAsyncSvc(requestData, {
          txCd: "TREVLS0024",
          success: async () => {
            message.success("삭제되었습니다.");
            await loadHrMapping({ showMsg: false });
          },
        });
      } catch (e) {
          
        message.error("삭제 중 오류가 발생했습니다.");
      } finally {
        common.showLoading(false);
      }
    },
    [isHrMappingLocked, lockMsg, loadHrMapping]
  );

  /************************************************
   * 테이블 컬럼
   ************************************************/
  const columns: ColumnsType<HrMapRow> = useMemo(
    () => [
      {
        title: "피평가자",
        key: "employee",
        width: 180,
        fixed: "left",
        align: "center",
        render: (_: any, r: HrMapRow) => {
          const name = r.userNm ?? "-";
          const pos = r.eePosClsCd ? posName(r.eePosClsCd) || "-" : "-";

          return (
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: 16 }}>{name}</div>
              <div style={{ fontSize: 12, color: "#888" }}>{pos}</div>
            </div>
          );
        },
      },
      {
        title: "사업부",
        dataIndex: "eeUpperDeptNm" as any,
        key: "eeUpperDeptNm",
        width: 160,
        align: "center",
        render: (v: string) => v ?? "-",
      },
      {
        title: "실/팀",
        dataIndex: "deptNm",
        key: "deptNm",
        width: 160,
        align: "center",
        render: (v: string) => v ?? "-",
      },
      {
        title: "1차 평가자",
        key: "eval1",
        width: 220,
        align: "center",
        render: (_: any, r: HrMapRow) => {
          const nm = r.eval1EmpNm ?? r.eval1EmpNo ?? "지정";
          const dept = r.eval1DeptNm ?? "-";
          const pos = r.eval1PosClsCd ? posName(r.eval1PosClsCd) || r.eval1PosClsCd : "-";

          return (
            <Button type="link" onClick={() => openEvalPopup(r, "eval1")} style={{ padding: 0 }}>
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontSize: 16 }}>{nm}</div>
                <div style={{ fontSize: 13, color: "#666" }}>
                  {dept} / {pos}
                </div>
              </div>
            </Button>
          );
        },
      },
      {
        title: "2차 평가자",
        key: "eval2",
        width: 220,
        align: "center",
        render: (_: any, r: HrMapRow) => {
          const nm = r.eval2EmpNm ?? r.eval2EmpNo ?? "지정";
          const dept = r.eval2DeptNm ?? "-";
          const pos = r.eval2PosClsCd ? posName(r.eval2PosClsCd) || r.eval2PosClsCd : "-";

          return (
            <Button type="link" onClick={() => openEvalPopup(r, "eval2")} style={{ padding: 0 }}>
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontSize: 16 }}>{nm}</div>
                <div style={{ fontSize: 13, color: "#666" }}>
                  {dept} / {pos}
                </div>
              </div>
            </Button>
          );
        },
      },
      {
        title: "자기평가",
        dataIndex: "slfSubYn",
        key: "slfSubYn",
        width: 90,
        align: "center",
        render: (v: string) =>
          v === "Y" ? <Tag color="success">제출완료</Tag> : <Tag color="default">미제출</Tag>,
      },
      {
        title: "1차평가",
        dataIndex: "eval1SubYn",
        key: "eval1SubYn",
        width: 90,
        align: "center",
        render: (v: string) =>
          v === "Y" ? <Tag color="success">제출완료</Tag> : <Tag color="default">미제출</Tag>,
      },
      {
        title: "2차평가",
        dataIndex: "eval2SubYn",
        key: "eval2SubYn",
        width: 90,
        align: "center",
        render: (v: string) =>
          v === "Y" ? <Tag color="success">제출완료</Tag> : <Tag color="default">미제출</Tag>,
      },
      {
        title: "상태",
        key: "overallStatus",
        width: 90,
        align: "center",
        render: (_: any, r: HrMapRow) =>
          r.slfSubYn === "Y" && r.eval1SubYn === "Y" && r.eval2SubYn === "Y" ? (
            <Tag color="success" style={{ marginInlineEnd: 0 }}>
              완료
            </Tag>
          ) : (
            <Tag color="warning" style={{ marginInlineEnd: 0 }}>
              진행중
            </Tag>
          ),
      },
      {
        title: "관리",
        key: "action",
        width: 100,
        align: "center",
        render: (_: any, row: any) => (
          <Popconfirm
            title="해당 데이터를 삭제하시겠습니까?"
            okText="삭제"
            cancelText="취소"
            onConfirm={() => deleteMappingOne(row)}
          >
            <Button danger size="small" disabled={isHrMappingLocked || isEvalStarted(row)}>
              삭제
            </Button>
          </Popconfirm>
        ),
      },
    ],
    [deleteMappingOne, isHrMappingLocked, openEvalPopup, posName]
  );

  /************************************************
   * 미리보기 카운트
   ************************************************/
  const previewInvalidCount = uploadPreviewRows.filter((r) => !r._valid).length;
  const previewValidCount = uploadPreviewRows.filter((r) => r._valid).length;

  /************************************************
   * Render
   ************************************************/
  return (
    <div>
      <Row style={{ marginBottom: 10 }}>
        <Col flex="auto">
          {evlCycleExists ? (
            <Space wrap>
              <Tag color="blue">평가정보 등록됨</Tag>
              <Tag>인사평가 시작일자: {hrEvlStDt || "-"}</Tag>
              <Tag>인사평가 종료일자: {eval2EdDt || "-"}</Tag>
              {isHrMappingLocked && <Tag color="red">{lockMsg}</Tag>}
            </Space>
          ) : (
            <Tag color="red">평가정보가 등록되지 않았습니다.</Tag>
          )}
        </Col>
      </Row>

      {/* 검색 + 우측 엑셀 기능 */}
      <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 14 }}>
        <Col flex="auto">
          <Space wrap>
            <Select
              value={evlYear}
              onChange={onChangeYear}
              style={{ width: 110 }}
              options={yearOptions}
            />

            <Select
              value={empSearchKey}
              onChange={onChangeSearchKey}
              style={{ width: 180 }}
              options={[
                { label: "전체", value: "all" },
                { label: "피평가자명", value: "name" },
                { label: "피평가자 부서", value: "dept" },
                { label: "평가자명(1/2차)", value: "evaluator" },
              ]}
            />

            <Input
              placeholder="검색어 입력"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => onChangeSearchText(e.target.value)}
              style={{ width: 240 }}
              allowClear
              onPressEnter={onSearch}
            />

            <Button type="primary" icon={<SearchOutlined />} onClick={onSearch} loading={loading}>
              검색
            </Button>

            <Button onClick={onResetSearch} disabled={loading}>
              초기화
            </Button>

            <Button
              onClick={onCreateHrMappingQuery}
              loading={loading}
              disabled={isMappingModifyBlocked}
            >
              매핑 자동생성
            </Button>
          </Space>
        </Col>

        <Col flex="none">
          <Space wrap style={{ justifyContent: "flex-end" }}>
            <Upload
              accept=".xlsx,.xls"
              showUploadList={false}
              disabled={isMappingModifyBlocked}
              multiple={false}
              beforeUpload={async (file) => {
                try {
                  common.showLoading(true);

                  const preview = await parseHrExcelForPreview(file as File);
                  setUploadPreviewFileName((file as any)?.name ?? "");
                  setUploadPreviewRows(preview);
                  setUploadPreviewOpen(true);

                  if (preview.length === 0) message.warning("업로드할 데이터가 없습니다.");
                } catch (e: any) {
                    
                  message.error(e?.message || "엑셀 파싱 중 오류가 발생했습니다.");
                } finally {
                  common.showLoading(false);
                }
                return false;
              }}
            >
              <Button className="excel-btn" icon={<UploadOutlined />} disabled={isMappingModifyBlocked}>
                템플릿 업로드
              </Button>
            </Upload>

            <Button className="excel-btn" icon={<DownloadOutlined />} onClick={onDownloadTemplate} loading={loading}>
              템플릿 다운로드
            </Button>

            <Button className="excel-btn" icon={<DownloadOutlined />} onClick={onDownloadHrExcel} loading={loading}>
              엑셀 다운로드
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={rows}
        rowKey="hrMapId"
        loading={loading}
        size="middle"
        style={{ fontSize: 15 }}
        rowClassName={() => "evl0109-row"}
        scroll={{ x: 1000 }}
        pagination={{
          current: pageNo,
          pageSize: 10,
          showSizeChanger: false,
          onChange: (page) => setPageNo(page),
        }}
        bordered
      />

      {/* ✅ 업로드 미리보기 모달 */}
      <Modal
        open={uploadPreviewOpen}
        title={`엑셀 업로드 미리보기 - ${uploadPreviewFileName}`}
        width={1050}
        centered
        okText="업로드"
        cancelText="취소"
        okButtonProps={{
          loading: uploading,
          disabled: previewValidCount === 0,
        }}
        onOk={commitHrExcelUpload}
        onCancel={() => {
          if (uploading) return;
          setUploadPreviewOpen(false);
          setUploadPreviewRows([]);
          setUploadPreviewFileName("");
          setOnlyExcluded(false);
        }}
      >
        <Space style={{ marginBottom: 12 }} wrap>
          <Tag>총 {uploadPreviewRows.length}건</Tag>

          <Tag color={previewValidCount > 0 ? "success" : "default"}>
            업로드 대상 {previewValidCount}건
          </Tag>

          <Tag color={previewInvalidCount > 0 ? "warning" : "success"}>
            제외 {previewInvalidCount}건
          </Tag>

          <Switch
            checked={onlyExcluded}
            onChange={setOnlyExcluded}
            checkedChildren="제외만"
            unCheckedChildren="전체"
            disabled={previewInvalidCount === 0}
          />

          {previewInvalidCount > 0 && (
            <span style={{ color: "#ad6800" }}>오류가 있는 행은 제외하고 업로드됩니다.</span>
          )}
        </Space>

        <Table
          size="small"
          rowKey="key"
          dataSource={previewTableRows}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
          }}
          scroll={{ x: 900 }}
          columns={[
            {
              title: "행",
              dataIndex: "_rowNo",
              width: 70,
              align: "center",
            },
            {
              title: "피평가자",
              key: "employee",
              width: 260,
              align: "center",
              render: (_: any, r: any) => {
                const name = r.empNm || "-";
                const dept = r.empDeptNm || "-";
                const pos = r.empPosNm || "-";

                return (
                  <div style={{ lineHeight: 1.2, textAlign: "center" }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{name}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      {dept} / {pos}
                    </div>
                  </div>
                );
              },
            },
            {
              title: "1차 평가자",
              key: "eval1",
              width: 200,
              align: "center",
              render: (_: any, r: any) => {
                const name = r.eval1Nm || "-";
                return (
                  <div style={{ lineHeight: 1.2, textAlign: "center" }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{name}</div>
                  </div>
                );
              },
            },
            {
              title: "2차 평가자",
              key: "eval2",
              width: 200,
              align: "center",
              render: (_: any, r: any) => {
                const name = r.eval2Nm || "-";
                return (
                  <div style={{ lineHeight: 1.2, textAlign: "center" }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{name}</div>
                  </div>
                );
              },
            },
            {
              title: "확인",
              width: 220,
              align: "center",
              render: (_: any, r: any) => {
                if (r._valid) return <Tag color="success">유효</Tag>;

                return (
                  <div style={{ color: "#cf1322", textAlign: "center" }}>
                    {(r._errors ?? []).map((err: string, i: number) => (
                      <div key={i}>{err}</div>
                    ))}
                  </div>
                );
              },
            },
          ]}
          bordered
        />
      </Modal>

      <style>{`
        .evl0109-row td { padding-top: 12px !important; padding-bottom: 12px !important; }
        .evl0109-row .ant-btn-link { font-size: 15px !important; }
        .ant-table-thead > tr > th { font-size: 14px !important; padding-top: 10px !important; padding-bottom: 10px !important; }

        /* ✅ 엑셀 버튼 초록 톤 */
        .excel-btn {
          border-color: #52c41a !important;
          color: #389e0d !important;
        }
        .excel-btn:hover,
        .excel-btn:focus {
          border-color: #73d13d !important;
          color: #52c41a !important;
        }
      `}</style>
    </div>
  );
}

/************************************************
 * 프로젝트 탭 컨테이너 (년도 분리)
 ************************************************/
function ProjectEvaluationTableContainer(props: CycleProps) {
  const { evlCycleExists, hrEvlStDt, eval2EdDt, isMappingModifyBlocked, lockMsg } = props;

  const [pjEvlYear, setPjEvlYear] = useState<string>("");
  const [pjEvlYearList, setPjEvlYearList] = useState<string[]>([]);

  useEffect(() => {
    if (!pjEvlYear) setPjEvlYear(String(new Date().getFullYear()));
  }, [pjEvlYear]);

  return (
    <ProjectEvaluationTable
      evlYear={pjEvlYear}
      setEvlYear={setPjEvlYear}
      evlYearList={pjEvlYearList}
      setEvlYearList={setPjEvlYearList}
      evlCycleExists={evlCycleExists}
      hrEvlStDt={hrEvlStDt}
      eval2EdDt={eval2EdDt}
      isMappingModifyBlocked={isMappingModifyBlocked}
      lockMsg={lockMsg}
    />
  );
}

/************************************************
 * 메인 컨테이너
 ************************************************/
export default function Evl0109m() {
  /************************************************
   * 공통(사이클/잠금) 상태는 부모에서 관리
   ************************************************/
  const [hrEvlStDt, setHrEvlStDt] = useState<string>("");
  const [bizPlgStDt, setBizPlgStDt] = useState<string>("");
  const [eval2EdDt, setEval2EdDt] = useState<string>("");
  const [evlCycleExists, setEvlCycleExists] = useState<boolean>(false);

  // ✅ YYYY-MM-DD 문자열을 Date(00:00)로
  const parseYmdDash = useCallback((ymdDash?: string) => {
    if (!ymdDash) return null;
    const d = new Date(`${ymdDash}T00:00:00`);
    return isNaN(d.getTime()) ? null : d;
  }, []);

  const isHrMappingLocked = useMemo(() => {
    if (!evlCycleExists) return true; // 사이클 없으면 아예 수정 불가

    const end = parseYmdDash(eval2EdDt);
    if (!end) return true;

    const lockAt = new Date(end);
    lockAt.setDate(lockAt.getDate() + 14);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return today > lockAt;
  }, [evlCycleExists, eval2EdDt, parseYmdDash]);

  const lockMsg = useMemo(() => {
    if (!evlCycleExists)
      return "평가 사이클이 등록되지 않아 매핑을 변경할 수 없습니다.";
    if (!eval2EdDt) return "인사평가 종료일자가 없어 매핑을 변경할 수 없습니다.";
    return `인사평가 종료(${eval2EdDt}) 2주 이후에는 매핑을 변경할 수 없습니다.`;
  }, [evlCycleExists, eval2EdDt]);

  const isMappingModifyBlocked = useMemo(() => {
    return !evlCycleExists || isHrMappingLocked;
  }, [evlCycleExists, isHrMappingLocked]);

  const loadEvlCycleInfo = useCallback(() => {
    const y = String(new Date().getFullYear());
    const requestData = { evlYear: y };

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0003",
      success: (responseData: any) => {
        const items = responseData?.EvlCycleItems ?? responseData?.data?.EvlCycleItems ?? [];
        const first = items?.[0];

        // ✅ 등록 여부
        setEvlCycleExists(items.length > 0);

        const rawHr = first?.hrEvlStDt ?? "";
        const rawBiz = first?.bizPlgStDt ?? "";
        const rawEval2Ed = first?.eval2EdDt ?? "";

        const formatYmd = (ymd?: string) => {
          if (!ymd || String(ymd).length !== 8) return "";
          const s = String(ymd);
          return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
        };

        setHrEvlStDt(formatYmd(rawHr));
        setBizPlgStDt(formatYmd(rawBiz));
        setEval2EdDt(formatYmd(rawEval2Ed));
      },
      error: (msg: string) => {
        message.error(msg);
        setEvlCycleExists(false);
        setHrEvlStDt("");
        setBizPlgStDt("");
        setEval2EdDt("");
      },
    });
  }, []);

  useEffect(() => {
    loadEvlCycleInfo();
  }, [loadEvlCycleInfo]);

  const cycleProps: CycleProps = useMemo(
    () => ({
      evlCycleExists,
      hrEvlStDt,
      bizPlgStDt,
      eval2EdDt,
      isMappingModifyBlocked,
      isHrMappingLocked,
      lockMsg,
    }),
    [
      evlCycleExists,
      hrEvlStDt,
      bizPlgStDt,
      eval2EdDt,
      isMappingModifyBlocked,
      isHrMappingLocked,
      lockMsg,
    ]
  );

  const items = useMemo(
    () => [
      {
        key: "1",
        label: (
          <span>
            <FileTextOutlined /> 인사평가
          </span>
        ),
        children: <HrEvaluationTable {...cycleProps} />,
      },
      {
        key: "2",
        label: (
          <span>
            <ProjectOutlined /> 프로젝트 평가
          </span>
        ),
        children: <ProjectEvaluationTableContainer {...cycleProps} />,
      },
    ],
    [cycleProps]
  );

  return (
    <div style={{ fontSize: "14px" }}>
      <Typography.Title level={2} style={{ textAlign: "center", marginBottom: 16 }}>
        평가대상 매핑관리
      </Typography.Title>
      <Tabs defaultActiveKey="1" items={items} size="large" />
    </div>
  );
}